import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ExternalLink, MapPin, Calendar, BadgeCheck } from 'lucide-react';
import { fetchResource, fetchRelated, API } from '../api/client';

export default function CompanyPage() {
  const { slug } = useParams();
  const [co, setCo] = useState(null);
  const [related, setRelated] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setCo(null);
    setErr(null);
    fetchResource(slug)
      .then(setCo)
      .catch(() => setErr('Company not found'));
    fetchRelated(slug, 8).then(setRelated).catch(() => setRelated([]));
  }, [slug]);

  if (err) return <div className="page-container"><div className="empty-state">{err}</div></div>;
  if (!co) return <div className="page-container"><div className="empty-state">Loading…</div></div>;

  const attrs = co.attributes || {};
  const initial = co.name.trim().charAt(0).toUpperCase();

  return (
    <div className="page-container">
      <div className="crumbs">
        <Link to="/">Home</Link>
        <span>/</span>
        <span>Companies</span>
        <span>/</span>
        <span>{co.name}</span>
      </div>

      <div className="tool-page-head" data-testid="company-page-head">
        <div className="tool-page-logo" aria-hidden>
          {co.logo ? <img src={co.logo} alt="" /> : <span>{initial}</span>}
        </div>
        <div className="tool-page-title">
          <h1>{co.name}</h1>
          <p className="tool-page-desc">{co.short_description}</p>
          <div className="tool-badges">
            <span className="badge chip-t">Company</span>
            {co.verified && <span className="badge chip-t"><BadgeCheck size={10} /> Verified</span>}
          </div>
        </div>
        {co.website_url && (
          <a
            className="pill-btn primary lg"
            href={`${API}/go/${co.slug}?ref=/company/${co.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="company-visit-btn"
          >
            Visit site <ExternalLink size={14} />
          </a>
        )}
      </div>

      <div className="tool-page-grid">
        <div className="tool-page-main">
          <h2>About</h2>
          <p>{co.full_description || co.short_description}</p>

          {related.length > 0 && (
            <>
              <h3 style={{ marginTop: 24 }}>Related</h3>
              <ul className="supp-list">
                {related.map((r) => (
                  <li key={r.slug}>
                    <Link to={r.resource_type === 'company' ? `/company/${r.slug}` : `/tool/${r.slug}`} className="supp-link">
                      {r.name}
                    </Link>
                    <span className="supp-cat">{r.resource_type}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <aside className="tool-page-side">
          {attrs.hq && (
            <div className="side-block">
              <span className="muted"><MapPin size={12} /> Headquarters</span>
              <div className="side-value">{attrs.hq}</div>
            </div>
          )}
          {attrs.founded && (
            <div className="side-block">
              <span className="muted"><Calendar size={12} /> Founded</span>
              <div className="side-value">{attrs.founded}</div>
            </div>
          )}
          {co.website_url && (
            <div className="side-block">
              <span className="muted">Website</span>
              <a
                className="side-link"
                href={`${API}/go/${co.slug}?ref=/company/${co.slug}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {co.website_url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
              </a>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
